// src/lib/arbYield.js

import { dayCountFraction } from "lib/dayCount";

/**
 * Flow-based arbitrage yield calculation.
 *
 * Core idea:
 *  - Use actual flowRows for interest and scheduled principal.
 *  - For premium callable bonds, move principal from maturity date to call date.
 *  - Solve yield against aggregate issue price less arb-yield adjustments.
 */

function toISODate(d) {
  if (!d) return "";
  return String(d).slice(0, 10);
}

function computeTotalIssuePrice(bondRows = []) {
  return bondRows.reduce((sum, r) => {
    const par = Number(r.principal) || 0;
    const pricePer100 = Number(r.price);
    if (!par || !Number.isFinite(pricePer100) || pricePer100 <= 0) return sum;
    return sum + par * (pricePer100 / 100);
  }, 0);
}

function isPremiumCallableBond(bond = {}) {
  const par = Number(bond.principal) || 0;
  const pricePer100 = Number(bond.price);
  const callPricePer100 =
    bond.call_price != null ? Number(bond.call_price) : 100;

  const maturityDate = toISODate(bond.date);
  const callDate = toISODate(bond.call_date);

  if (!par || !maturityDate || !callDate) return false;
  if (!Number.isFinite(pricePer100) || pricePer100 <= 0) return false;

  return pricePer100 > callPricePer100;
}

function buildFlowMap(flowRows = []) {
  const map = new Map();

  for (const r of flowRows) {
    const date = toISODate(r.date);
    if (!date) continue;

    if (!map.has(date)) {
      map.set(date, {
        date,
        interest: 0,
        principal: 0,
      });
    }

    const row = map.get(date);
    row.interest += Number(r.interest) || 0;
    row.principal += Number(r.principal) || 0;
  }

  return map;
}

function applyCallablePremiumRedemptions(flowMap, bondRows = []) {
  for (const bond of bondRows) {
    if (!isPremiumCallableBond(bond)) continue;

    const par = Number(bond.principal) || 0;
    const couponPct = Number(bond.coupon) || 0;
    const maturityDate = toISODate(bond.date);
    const callDate = toISODate(bond.call_date);
    const callPricePer100 =
      bond.call_price != null ? Number(bond.call_price) : 100;

    const redemptionAmount = par * (callPricePer100 / 100);
    const semiCoupon = ((couponPct / 100) * par) / 2;

    const maturityRow = flowMap.get(maturityDate);

    if (!maturityRow || (Number(maturityRow.principal) || 0) < par - 0.01) {
      console.warn("[ArbYield] Skipping call adjustment", {
        maturityDate,
        callDate,
        par,
        maturityPrincipal: maturityRow?.principal,
      });
      continue;
    }

    // 1. Remove scheduled principal from original maturity.
    maturityRow.principal -= par;

    // 2. Add redemption principal to call date.
    if (!flowMap.has(callDate)) {
      flowMap.set(callDate, {
        date: callDate,
        interest: 0,
        principal: 0,
      });
    }

    flowMap.get(callDate).principal += redemptionAmount;

    // 3. Remove interest AFTER call date through original maturity.
    for (const row of flowMap.values()) {
      const d = toISODate(row.date);

      if (d > callDate && d <= maturityDate) {
        row.interest -= semiCoupon;
      }
    }

  }
}

function buildAdjustedCashFlows({
  flowRows = [],
  bondRows = [],
  issueDate,
  dayCountBasis = "30/360",
}) {
  const flowMap = buildFlowMap(flowRows);

  applyCallablePremiumRedemptions(flowMap, bondRows);

  return Array.from(flowMap.values())
    .map((r) => {
      const interest = Number(r.interest) || 0;
      const principal = Number(r.principal) || 0;
      const total = interest + principal;

      return {
        date: r.date,
        interest,
        principal,
        total,
        t: dayCountFraction(issueDate, r.date, dayCountBasis),
        amount: -total,
      };
    })
    .filter((r) => r.date && Math.abs(r.total) > 0.005 && r.t > 0)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

function pvAtNominalSemiannualRate(rate, flows = []) {
  return flows.reduce((sum, f) => {
    const t = Number(f.t) || 0;
    const total = Number(f.total) || 0;

    if (t <= 0 || !Number.isFinite(total)) return sum;

    return sum + total / Math.pow(1 + rate / 2, 2 * t);
  }, 0);
}

function solveNominalSemiannualRate(target, flows = []) {
  if (!Number.isFinite(target) || target <= 0 || !flows.length) return null;

  const f = (rate) => pvAtNominalSemiannualRate(rate, flows) - target;

  let low = -0.99;
  let high = 1.0;

  let fLow = f(low);
  let fHigh = f(high);

  let attempts = 0;
  while (fLow * fHigh > 0 && attempts < 20) {
    high += 1.0;
    fHigh = f(high);
    attempts += 1;
  }

  if (fLow * fHigh > 0) return null;

  for (let i = 0; i < 100; i++) {
    const mid = (low + high) / 2;
    const fMid = f(mid);

    if (Math.abs(fMid) < 0.000001) return mid;

    if (fLow * fMid < 0) {
      high = mid;
      fHigh = fMid;
    } else {
      low = mid;
      fLow = fMid;
    }
  }

  return (low + high) / 2;
}

export function computeArbYield({
  bondRows = [],
  flowRows = [],
  issueDate,
  dayCountBasis = "30/360",
  issuePrice,
  issuePriceAdjustment = 0,
}) {
  if (!bondRows.length || !flowRows.length || !issueDate) return null;

  const targetIssuePrice =
    Number(issuePrice) > 0
      ? Number(issuePrice)
      : computeTotalIssuePrice(bondRows);

  const adjustedIssuePrice =
    targetIssuePrice - (Number(issuePriceAdjustment) || 0);

  if (!Number.isFinite(adjustedIssuePrice) || adjustedIssuePrice <= 0) {
    return null;
  }

  const adjustedFlows = buildAdjustedCashFlows({
    flowRows,
    bondRows,
    issueDate,
    dayCountBasis,
  });

  if (!adjustedFlows.length) return null;

  return solveNominalSemiannualRate(adjustedIssuePrice, adjustedFlows);
}
